import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { BookmarkLayout, Bookmark, BookmarkActionHandlers } from './bookmarks';
import { isAuthTokenValid, getAuthToken } from '../helper/token';

export const Dashboard: React.FC = () => {
  const history = useHistory();
  const [user, setUser] = useState<{ email: string; username: string } | null>(null);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [useGroupedLayout, setUseGroupedLayout] = useState(true); // Feature flag - set to true for grouped layouts

  useEffect(() => {
    // Check if user is logged in via auth token
    if (!isAuthTokenValid()) {
      history.push('/login');
      return;
    }

    // Decode user info from JWT payload
    const token = getAuthToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({ email: payload.email || '', username: payload.username || payload.email || '' });
      } catch {
        history.push('/login');
        return;
      }
    }
    
    // Load dummy bookmarks
    const dummyBookmarks: Bookmark[] = [
      {
        id: '1',
        title: 'React Documentation',
        url: 'https://reactjs.org/docs',
        description: 'A JavaScript library for building user interfaces',
        createdAt: '2024-01-15T10:30:00Z',
        tags: ['react', 'javascript', 'documentation'],
        favicon: 'https://reactjs.org/favicon.ico'
      },
      {
        id: '2',
        title: 'GitHub - The world\'s leading software development platform',
        url: 'https://github.com',
        description: 'GitHub is where over 100 million developers shape the future of software, together.',
        createdAt: '2024-01-15T15:45:00Z',
        tags: ['github', 'development', 'git'],
        favicon: 'https://github.com/favicon.ico'
      },
      {
        id: '3',
        title: 'Stack Overflow - Where Developers Learn, Share, & Build Careers',
        url: 'https://stackoverflow.com',
        description: 'Stack Overflow is the largest, most trusted online community for developers.',
        createdAt: '2024-01-13T09:15:00Z',
        tags: ['stackoverflow', 'programming', 'community'],
        favicon: 'https://stackoverflow.com/favicon.ico'
      },
      {
        id: '4',
        title: 'MDN Web Docs',
        url: 'https://developer.mozilla.org',
        description: 'Resources for developers, by developers.',
        createdAt: '2024-01-12T14:20:00Z',
        tags: ['mdn', 'web', 'documentation', 'javascript'],
        favicon: 'https://developer.mozilla.org/favicon-48x48.png'
      },
      {
        id: '5',
        title: 'TypeScript: JavaScript With Syntax For Types',
        url: 'https://www.typescriptlang.org',
        description: 'TypeScript extends JavaScript by adding types.',
        createdAt: '2024-01-11T11:00:00Z',
        tags: ['typescript', 'javascript', 'types'],
        favicon: 'https://www.typescriptlang.org/favicon-32x32.png'
      },
      {
        id: '6',
        title: 'Example Article: Best Practices for Web Development',
        url: 'https://example-site.com/article/best-practices',
        description: 'A comprehensive guide covering modern web development practices, performance optimization, and accessibility standards.',
        createdAt: '2024-01-10T16:22:00Z',
        tags: ['web-development', 'best-practices', 'performance'],
        // No favicon property - will show generic icon
      },
      {
        id: '7',
        // No title - demonstrating pages without titles
        url: 'https://api.example.com/v1/data',
        description: 'API endpoint that returns JSON data for testing purposes.',
        createdAt: '2024-01-09T08:30:00Z',
        tags: ['api', 'testing', 'json'],
      }
    ];
    
    setBookmarks(dummyBookmarks);
  }, [history]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    history.push('/');
  };

  const handleDeleteBookmark = (bookmarkId: string) => {
    if (window.confirm('Are you sure you want to delete this bookmark?')) {
      setBookmarks(prev => prev.filter(bookmark => bookmark.id !== bookmarkId));
    }
  };

  const handleOpenExternal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleAddTag = (bookmarkId: string) => {
    const newTag = prompt('Enter a new tag:');
    if (newTag && newTag.trim()) {
      const trimmedTag = newTag.trim().toLowerCase();
      setBookmarks(prev => prev.map(bookmark => {
        if (bookmark.id === bookmarkId) {
          // Only add if tag doesn't already exist
          if (!bookmark.tags.includes(trimmedTag)) {
            return { ...bookmark, tags: [...bookmark.tags, trimmedTag] };
          }
        }
        return bookmark;
      }));
    }
  };

  const handleRemoveTag = (bookmarkId: string, tagToRemove: string) => {
    setBookmarks(prev => prev.map(bookmark => {
      if (bookmark.id === bookmarkId) {
        return { ...bookmark, tags: bookmark.tags.filter(tag => tag !== tagToRemove) };
      }
      return bookmark;
    }));
  };

  const handleAddToCollection = (bookmarkId: string) => {
    // TODO: Implement collection functionality
    alert('Add to collection - coming soon!');
  };

  const handleToggleFavorite = (bookmarkId: string) => {
    // TODO: Implement favorites functionality
    alert('Toggle favorite - coming soon!');
  };

  const bookmarkHandlers: BookmarkActionHandlers = {
    onDelete: handleDeleteBookmark,
    onOpenExternal: handleOpenExternal,
    onAddTag: handleAddTag,
    onRemoveTag: handleRemoveTag,
    onTagClick: setSelectedTag,
    onAddToCollection: handleAddToCollection,
    onToggleFavorite: handleToggleFavorite
  };

  const getAllTags = () => {
    const tags = new Set<string>();
    bookmarks.forEach(bookmark => {
      bookmark.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  };

  const filteredBookmarks = bookmarks.filter(bookmark => {
    const matchesSearch = (bookmark.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
                         bookmark.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bookmark.url.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTag = !selectedTag || bookmark.tags.includes(selectedTag);
    
    return matchesSearch && matchesTag;
  });

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="dashboard">
      {/* Dashboard Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-header-left">
            <h1>My Bookmarks</h1>
            <p>Welcome back, {user.username}!</p>
          </div>
          <div className="dashboard-header-right">
            <button onClick={handleLogout} className="logout-button">
              Log Out
            </button>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <div className="dashboard-content">
        {/* Search and Filter Bar */}
        <div className="dashboard-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search bookmarks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-container">
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="tag-filter"
            >
              <option value="">All Tags</option>
              {getAllTags().map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
          <div className="view-toggle">
            <button
              onClick={() => setViewMode('card')}
              className={`view-button ${viewMode === 'card' ? 'active' : ''}`}
              title="Card View"
            >
              <span role="img" aria-label="grid">⊞</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              title="List View"
            >
              <span role="img" aria-label="list">☰</span>
            </button>
            <button
              onClick={() => setUseGroupedLayout(!useGroupedLayout)}
              className={`view-button ${useGroupedLayout ? 'active' : ''}`}
              title={useGroupedLayout ? "Switch to ungrouped view" : "Group by date"}
            >
              <span role="img" aria-label="group by date">📅</span>
            </button>
          </div>
        </div>

        {/* Bookmarks Stats */}
        <div className="dashboard-stats">
          <div className="stat-card">
            <h3>{bookmarks.length}</h3>
            <p>Total Bookmarks</p>
          </div>
          <div className="stat-card">
            <h3>{getAllTags().length}</h3>
            <p>Tags</p>
          </div>
          <div className="stat-card">
            <h3>{filteredBookmarks.length}</h3>
            <p>Filtered Results</p>
          </div>
        </div>

        {/* Bookmarks Container */}
        <div className={`bookmarks-container ${viewMode === 'list' ? 'list-view' : 'card-view'} ${useGroupedLayout ? 'grouped-layout' : ''}`}>
          {filteredBookmarks.length === 0 ? (
            <div className="empty-state">
              <h3>No bookmarks found</h3>
              <p>
                {searchTerm || selectedTag 
                  ? 'Try adjusting your search or filter criteria.' 
                  : 'Start saving pages with the Qlipped button in your bookmark bar!'
                }
              </p>
            </div>
          ) : (
            <BookmarkLayout 
              bookmarks={filteredBookmarks}
              handlers={bookmarkHandlers}
              viewMode={viewMode}
              useGroupedLayout={useGroupedLayout}
            />
          )}
        </div>
      </div>
    </div>
  );
};